// TestCaseLibraryPOC.js — Documentation Fonctionnelle : POC « Bibliothèque de cas de test » (module Campagne).
// Aperçu INTERACTIVE de l'écran réel : en-tête, barre de filtres, liste tabulaire (sélection multiple,
// statut/critique, tâche OU flux), détail au clic avec onglets (Informations / Entrée / Sortie), et
// formulaire de création (tâche XOR TASK_FLOW). Les actions sont SIMULÉES localement — aucun appel réseau.
// Source de vérité : automata-studio-frontend [CPG][03]-TestCaseLibrary.js + automata_campaign TestCaseController.
import React, { useState } from 'react';
import {
  FileText, Plus, X, Save, GitBranch, Code, AlertTriangle, CheckCircle, XCircle,
  Clock, Calendar, User, Hash, Info, ArrowRight, Target, ListChecks, ClipboardList,
  Search, List as ListIcon, Grid as GridIcon, RefreshCw, Eye, Copy, Edit, Link2, Check,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Tabs, Icon, SearchBar, EmptyState } from '../ui';

// Deux types d'élément exécuté par un cas de test (mutuellement exclusifs : task XOR taskFlow).
const ITEM_TYPES = [
  { value: 'TASK', label: 'Tâche simple', icon: 'Code', color: '#3B82F6' },
  { value: 'TASK_FLOW', label: 'Enchaînement (TASK_FLOW)', icon: 'GitBranch', color: '#0EA5E9' },
];

// Référentiels en dur pour la sélection (tâche XOR flux).
const SAMPLE_TASKS = [
  { id: 11, taskId: 'TSK-00021', taskName: 'Saisie virement domestique' },
  { id: 12, taskId: 'TSK-00034', taskName: 'Consultation solde compte' },
  { id: 13, taskId: 'TSK-00047', taskName: 'Validation niveau 2' },
];
const SAMPLE_FLOWS = [
  { id: 91, taskFlowId: 'FLOW-0007', name: 'Cycle complet virement', steps: 4 },
  { id: 92, taskFlowId: 'FLOW-0012', name: 'Ouverture + alimentation compte', steps: 3 },
];

// Filtres de la barre supérieure (miroir de l'écran réel).
const ACTIVE_FILTERS = [
  { label: 'Tous', value: 'all' },
  { label: 'Actifs', value: 'active' },
  { label: 'Inactifs', value: 'inactive' },
];
const CRITICAL_FILTERS = [
  { label: 'Tous', value: 'all' },
  { label: 'Critiques', value: 'critical' },
  { label: 'Non critiques', value: 'normal' },
];

const SAMPLE = [
  {
    id: 1, testCaseId: 'TC-1A2B3C4D', name: 'Virement domestique nominal', isActive: true, isCritical: true,
    description: 'Émission d\'un virement domestique standard validé jusqu\'au règlement.',
    preconditions: 'Compte débiteur provisionné ; bénéficiaire enregistré.',
    expectedResult: 'Virement comptabilisé et message de confirmation affiché.',
    executionDuration: 30,
    item: { type: 'TASK', ref: SAMPLE_TASKS[0] },
    inputParam: { testScenario: 'virement_standard', expectedFields: 'montant,beneficiaire,motif' },
    outputParam: { interface: 'confirmation', fieldsVisible: 'true' },
    createdBy: 'admin', creationDate: '2026-06-10', lastModifiedDate: '2026-06-14',
  },
  {
    id: 2, testCaseId: 'TC-5E6F7A8B', name: 'Cycle complet virement (bout en bout)', isActive: true, isCritical: false,
    description: 'Scénario d\'enchaînement couvrant saisie, validation et règlement.',
    preconditions: 'Environnement de recette disponible ; agent déployé.',
    expectedResult: 'Toutes les étapes du flux passent au statut « Terminé ».',
    executionDuration: 90,
    item: { type: 'TASK_FLOW', ref: SAMPLE_FLOWS[0] },
    inputParam: { testScenario: 'cycle_complet' },
    outputParam: {},
    createdBy: 'admin', creationDate: '2026-06-11', lastModifiedDate: '2026-06-12',
  },
  {
    id: 3, testCaseId: 'TC-9C0D1E2F', name: 'Consultation solde (lecture seule)', isActive: false, isCritical: false,
    description: 'Vérification de l\'affichage du solde sans mouvement.',
    preconditions: 'Compte existant.',
    expectedResult: 'Le solde courant s\'affiche correctement.',
    executionDuration: 15,
    item: { type: 'TASK', ref: SAMPLE_TASKS[1] },
    inputParam: {},
    outputParam: { interface: 'accessible' },
    createdBy: 'ops', creationDate: '2026-06-09', lastModifiedDate: '2026-06-09',
  },
];

const EMPTY_FORM = {
  name: '', description: '', preconditions: '', expectedResult: '', executionDuration: 30,
  isCritical: false, isActive: true, testItemType: 'TASK', taskId: '', taskFlowId: '',
  inScenario: '', inFields: '', outInterface: '', outFields: '',
};

// ---- Petits sous-composants de la réplique ---------------------------------
const itemTypeOf = (v) => ITEM_TYPES.find((t) => t.value === v) || ITEM_TYPES[0];

const ItemBadge = ({ item }) => {
  if (!item) return <Badge>Aucune tâche</Badge>;
  const t = itemTypeOf(item.type);
  const lbl = item.type === 'TASK_FLOW' ? (item.ref?.name || item.ref?.taskFlowId) : (item.ref?.taskName || item.ref?.taskId);
  return <Badge color={t.color}><Icon name={t.icon} className="h-3 w-3" />{lbl}</Badge>;
};

const StatusPills = ({ tc }) => (
  <div className="flex items-center gap-1.5">
    {tc.isActive
      ? <Badge color="#10B981"><CheckCircle className="h-3 w-3" />Actif</Badge>
      : <Badge><XCircle className="h-3 w-3" />Inactif</Badge>}
    {tc.isCritical && <Badge color="#F59E0B"><AlertTriangle className="h-3 w-3" />Critique</Badge>}
  </div>
);

const InfoRow = ({ icon: IconCmp, label, value }) => (
  <div className="flex items-center justify-between px-3 py-2 text-sm">
    <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400"><IconCmp className="h-4 w-4" />{label}</span>
    <span className="text-gray-800 dark:text-gray-200 break-all text-right">{value || 'N/A'}</span>
  </div>
);

const GuideStep = ({ n, children }) => (
  <li className="flex items-start gap-3">
    <span className="shrink-0 w-6 h-6 rounded-full bg-blue-500 text-white text-xs font-bold flex items-center justify-center">{n}</span>
    <span className="text-sm text-gray-700 dark:text-gray-300">{children}</span>
  </li>
);

// ---- Page -------------------------------------------------------------------
const TestCaseLibraryPOC = () => {
  const [testCases, setTestCases] = useState(SAMPLE);
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('list'); // 'list' | 'detail' | 'create'
  const [tab, setTab] = useState('info'); // 'info' | 'input' | 'output'
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [criticalFilter, setCriticalFilter] = useState('all');
  const [taskFilter, setTaskFilter] = useState('');
  const [checked, setChecked] = useState([]); // sélection multiple (assignation campagne)
  const [refreshing, setRefreshing] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  const selected = testCases.find((tc) => tc.id === selectedId) || null;
  const filtered = testCases.filter((tc) =>
    (activeFilter === 'all' || (activeFilter === 'active' ? tc.isActive : !tc.isActive)) &&
    (criticalFilter === 'all' || (criticalFilter === 'critical' ? tc.isCritical : !tc.isCritical)) &&
    (!taskFilter || (tc.item?.type === 'TASK' && String(tc.item.ref?.id) === taskFilter)) &&
    (`${tc.name} ${tc.testCaseId} ${tc.description} ${tc.preconditions}`.toLowerCase().includes(search.toLowerCase()))
  );

  const openCreate = () => { setForm(EMPTY_FORM); setMode('create'); };
  const openDetail = (id) => { setSelectedId(id); setMode('detail'); setTab('info'); };
  const simulateRefresh = () => { setRefreshing(true); setTimeout(() => setRefreshing(false), 600); };

  const toggleCheck = (id) => setChecked((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const duplicate = (tc) => {
    const id = Math.max(0, ...testCases.map((x) => x.id)) + 1;
    setTestCases((prev) => [...prev, { ...tc, id, testCaseId: `TC-${id.toString().padStart(8, '0')}`, name: `${tc.name} (Copie)`, createdBy: 'vous', creationDate: '2026-06-16', lastModifiedDate: '2026-06-16' }]);
  };

  // task XOR taskFlow : selon le type choisi, une seule référence est conservée.
  const buildItem = (f) => f.testItemType === 'TASK_FLOW'
    ? (f.taskFlowId ? { type: 'TASK_FLOW', ref: SAMPLE_FLOWS.find((x) => String(x.id) === String(f.taskFlowId)) } : null)
    : (f.taskId ? { type: 'TASK', ref: SAMPLE_TASKS.find((x) => String(x.id) === String(f.taskId)) } : null);

  const itemMissing = form.testItemType === 'TASK_FLOW' ? !form.taskFlowId : !form.taskId;
  const canSubmit = !!form.name && !itemMissing;

  const submitCreate = () => {
    if (!canSubmit) return;
    const id = Math.max(0, ...testCases.map((tc) => tc.id)) + 1;
    const inputParam = {};
    if (form.inScenario) inputParam.testScenario = form.inScenario;
    if (form.inFields) inputParam.expectedFields = form.inFields;
    const outputParam = {};
    if (form.outInterface) outputParam.interface = form.outInterface;
    if (form.outFields) outputParam.fieldsVisible = form.outFields;
    const created = {
      id,
      testCaseId: `TC-${id.toString().padStart(8, '0')}`,
      name: form.name, description: form.description, preconditions: form.preconditions,
      expectedResult: form.expectedResult, executionDuration: parseInt(form.executionDuration, 10) || 30,
      isActive: form.isActive, isCritical: form.isCritical,
      item: buildItem(form),
      inputParam, outputParam,
      createdBy: 'vous', creationDate: '2026-06-16', lastModifiedDate: '2026-06-16',
    };
    setTestCases((prev) => [...prev, created]);
    openDetail(id);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const pickType = (v) => setForm((f) => ({ ...f, testItemType: v, taskId: '', taskFlowId: '' }));

  const inputClass = 'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 rounded-md focus:ring-2 focus:ring-violet-500';
  const pillClass = (on) => `px-3 py-1.5 rounded-xl text-xs font-medium transition-colors ${on ? 'bg-violet-500 text-white shadow-sm' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'}`;

  return (
    <div className="space-y-8">
      {/* ===== Intro ===== */}
      <div>
        <div className="flex items-center gap-2 flex-wrap">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Bibliothèque de cas de test</h1>
          <Badge color="#F59E0B">Module Campagne</Badge>
          <Badge color="#3B82F6">POC interactif</Badge>
        </div>
        <p className="mt-2 text-gray-600 dark:text-gray-300">Aperçu interactif de l'écran <strong>Bibliothèque de cas de test</strong> (démo, actions simulées localement).</p>
      </div>

      {/* ===== Réplique interactive de l'IHM ===== */}
      <Card className="overflow-hidden">
        {/* En-tête type app (gradient violet) */}
        <div className="px-5 py-4 bg-gradient-to-r from-violet-500 to-purple-700 text-white flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-xl bg-white/20 flex items-center justify-center shrink-0"><FileText className="h-6 w-6" /></span>
            <div>
              <div className="flex items-center gap-2">
                <h2 className="text-lg font-bold leading-tight">Bibliothèque de cas de test</h2>
                <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-white/20">{testCases.length}</span>
              </div>
              <p className="text-xs text-violet-100">Gestion et organisation des cas de test</p>
            </div>
          </div>
          <Button onClick={openCreate} className="bg-white/20 hover:bg-white/30 text-white border-0 shrink-0"><Plus className="h-4 w-4" />Nouveau cas de test</Button>
        </div>

        {/* Barre de filtres */}
        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-[200px] max-w-xs">
              <SearchBar value={search} onChange={setSearch} placeholder="Rechercher des cas de test..." />
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1 hidden sm:inline">Statut :</span>
              {ACTIVE_FILTERS.map((f) => (
                <button key={f.value} onClick={() => setActiveFilter(f.value)} className={pillClass(activeFilter === f.value)}>{f.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1 hidden sm:inline">Criticité :</span>
              {CRITICAL_FILTERS.map((f) => (
                <button key={f.value} onClick={() => setCriticalFilter(f.value)} className={pillClass(criticalFilter === f.value)}>{f.label}</button>
              ))}
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500 dark:text-gray-400 mr-1 hidden sm:inline">Tâche :</span>
              <select value={taskFilter} onChange={(e) => setTaskFilter(e.target.value)} className="text-xs rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-violet-400">
                <option value="">Toutes les tâches</option>
                {SAMPLE_TASKS.map((t) => <option key={t.id} value={t.id}>{t.taskName}</option>)}
              </select>
            </div>
            <div className="flex items-center gap-1 ml-auto">
              <div className="flex rounded-xl border border-gray-200 dark:border-gray-600 overflow-hidden">
                <button onClick={() => setViewMode('list')} className={`px-3 py-1.5 transition-colors ${viewMode === 'list' ? 'bg-violet-500 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}><ListIcon className="h-4 w-4" /></button>
                <button onClick={() => setViewMode('grid')} className={`px-3 py-1.5 transition-colors ${viewMode === 'grid' ? 'bg-violet-500 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}><GridIcon className="h-4 w-4" /></button>
              </div>
              <button onClick={simulateRefresh} className="p-2 rounded-xl text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"><RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} /></button>
            </div>
          </div>
        </div>

        {/* Corps : liste / création / détail */}
        <div className="p-5">
          {mode === 'create' ? (
            /* ---------- Formulaire de création ---------- */
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Plus className="h-5 w-5 text-violet-500" />Créer un nouveau cas de test</h3>
                <button onClick={() => setMode('list')} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="h-5 w-5" /></button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Nom du cas de test <span className="text-red-500">*</span></span>
                  <input value={form.name} onChange={set('name')} placeholder="Ex : Virement domestique nominal" className={inputClass} />
                </label>
                <label className="text-sm">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Durée d'exécution (min)</span>
                  <input type="number" value={form.executionDuration} onChange={set('executionDuration')} min={1} className={inputClass} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Description</span>
                  <textarea value={form.description} onChange={set('description')} rows={2} className={inputClass} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Pré-conditions</span>
                  <textarea value={form.preconditions} onChange={set('preconditions')} rows={2} placeholder="Ex : Compte provisionné ; agent déployé" className={inputClass} />
                </label>
                <label className="text-sm sm:col-span-2">
                  <span className="block text-gray-700 dark:text-gray-300 mb-1">Résultat attendu</span>
                  <textarea value={form.expectedResult} onChange={set('expectedResult')} rows={2} className={inputClass} />
                </label>
              </div>

              {/* Élément exécuté : tâche XOR flux */}
              <div className="mt-5">
                <div className="flex items-center gap-2 py-2">
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                  <span className="px-3 py-1 text-xs font-medium rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400">Élément exécuté <span className="text-red-500">*</span></span>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                </div>
                <div className="grid grid-cols-2 gap-2 mb-3">
                  {ITEM_TYPES.map((t) => {
                    const on = form.testItemType === t.value;
                    return (
                      <button key={t.value} type="button" onClick={() => pickType(t.value)}
                        className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        style={on ? { backgroundColor: t.color, borderColor: t.color, color: '#fff' } : undefined}>
                        <Icon name={t.icon} className="h-4 w-4" />{t.label}
                      </button>
                    );
                  })}
                </div>
                {form.testItemType === 'TASK' ? (
                  <select value={form.taskId} onChange={set('taskId')} className={inputClass}>
                    <option value="">— Sélectionner une tâche —</option>
                    {SAMPLE_TASKS.map((t) => <option key={t.id} value={t.id}>{t.taskName} ({t.taskId})</option>)}
                  </select>
                ) : (
                  <select value={form.taskFlowId} onChange={set('taskFlowId')} className={inputClass}>
                    <option value="">— Sélectionner un TASK_FLOW —</option>
                    {SAMPLE_FLOWS.map((t) => <option key={t.id} value={t.id}>{t.name} ({t.taskFlowId})</option>)}
                  </select>
                )}
              </div>

              {/* Paramètres d'entrée */}
              <div className="flex items-center gap-2 py-2 mt-3">
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">Paramètres d'entrée</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="text-sm"><span className="block text-gray-700 dark:text-gray-300 mb-1">Scénario de test</span><input value={form.inScenario} onChange={set('inScenario')} placeholder="Ex : virement_standard" className={inputClass} /></label>
                <label className="text-sm"><span className="block text-gray-700 dark:text-gray-300 mb-1">Champs attendus</span><input value={form.inFields} onChange={set('inFields')} placeholder="Ex : montant,beneficiaire,motif" className={inputClass} /></label>
              </div>

              {/* Paramètres de sortie */}
              <div className="flex items-center gap-2 py-2 mt-3">
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">Paramètres de sortie</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <label className="text-sm"><span className="block text-gray-700 dark:text-gray-300 mb-1">Interface</span><input value={form.outInterface} onChange={set('outInterface')} placeholder="Ex : confirmation" className={inputClass} /></label>
                <label className="text-sm"><span className="block text-gray-700 dark:text-gray-300 mb-1">Champs visibles</span><input value={form.outFields} onChange={set('outFields')} placeholder="Ex : true" className={inputClass} /></label>
              </div>

              {/* Options */}
              <div className="flex items-center gap-2 py-2 mt-3">
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                <span className="px-3 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">Options</span>
                <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-700 dark:text-gray-300">
                  <span>Cas de test actif</span>
                  <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))} className="h-4 w-4 accent-violet-500" />
                </label>
                <label className="flex items-center justify-between p-3 rounded-xl bg-gray-50 dark:bg-gray-700/50 text-sm text-gray-700 dark:text-gray-300">
                  <span>Test critique</span>
                  <input type="checkbox" checked={form.isCritical} onChange={(e) => setForm((f) => ({ ...f, isCritical: e.target.checked }))} className="h-4 w-4 accent-red-500" />
                </label>
              </div>

              <div className="flex justify-end gap-2 mt-5">
                <Button variant="ghost" onClick={() => setMode('list')}>Annuler</Button>
                <Button onClick={submitCreate} disabled={!canSubmit} className="bg-violet-500 hover:bg-violet-600 text-white"><Save className="h-4 w-4" />Créer</Button>
              </div>
              {!canSubmit && <p className="text-xs text-gray-400 mt-2">Champs requis : Nom et un élément exécuté ({form.testItemType === 'TASK_FLOW' ? 'un TASK_FLOW' : 'une tâche'}).</p>}
            </div>
          ) : mode === 'detail' && selected ? (
            /* ---------- Détail (panneau + onglets) ---------- */
            <div>
              <button onClick={() => setMode('list')} className="inline-flex items-center gap-1 text-sm text-violet-600 dark:text-violet-400 hover:underline mb-4"><ArrowRight className="h-4 w-4 rotate-180" />Retour à la liste</button>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="h-12 w-12 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0"><FileText className="h-6 w-6 text-white" /></span>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">{selected.name}</h3>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{selected.testCaseId}</div>
                  </div>
                </div>
                <StatusPills tc={selected} />
              </div>

              <div className="mb-4"><ItemBadge item={selected.item} /></div>

              <Tabs tabs={[{ id: 'info', label: 'Informations' }, { id: 'input', label: 'Entrée' }, { id: 'output', label: 'Sortie' }]} active={tab} onChange={setTab} className="mb-4" />

              {tab === 'info' ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1"><Info className="h-4 w-4" />Description</div>
                      <div className="text-sm text-gray-900 dark:text-gray-100">{selected.description || 'Aucune description'}</div>
                    </div>
                    <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1"><ListChecks className="h-4 w-4" />Pré-conditions</div>
                      <div className="text-sm text-gray-900 dark:text-gray-100 whitespace-pre-line">{selected.preconditions || 'Aucune'}</div>
                    </div>
                  </div>
                  <div className="p-3 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1"><Target className="h-4 w-4" />Résultat attendu</div>
                    <div className="text-sm text-gray-900 dark:text-gray-100">{selected.expectedResult || 'Non défini'}</div>
                  </div>
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-700/60">
                    <InfoRow icon={Hash} label="Référence" value={selected.testCaseId} />
                    <InfoRow icon={Clock} label="Durée d'exécution" value={`${selected.executionDuration} min`} />
                    <InfoRow icon={Calendar} label="Créé le" value={selected.creationDate} />
                    <InfoRow icon={User} label="Créé par" value={selected.createdBy} />
                    <InfoRow icon={Clock} label="Dernière modification" value={selected.lastModifiedDate} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="ghost" onClick={() => duplicate(selected)}><Copy className="h-4 w-4" />Dupliquer</Button>
                    <Button variant="outline"><Edit className="h-4 w-4" />Modifier</Button>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                  {(() => {
                    const params = tab === 'input' ? selected.inputParam : selected.outputParam;
                    const entries = Object.entries(params || {});
                    if (entries.length === 0) return <div className="p-6"><EmptyState icon={ClipboardList} title={`Aucun paramètre ${tab === 'input' ? "d'entrée" : 'de sortie'}`} /></div>;
                    return (
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                          <tr><th className="text-left font-semibold px-4 py-2">Paramètre</th><th className="text-left font-semibold px-4 py-2">Valeur</th></tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                          {entries.map(([k, v]) => <tr key={k}><td className="px-4 py-2 font-medium">{k}</td><td className="px-4 py-2">{String(v)}</td></tr>)}
                        </tbody>
                      </table>
                    );
                  })()}
                </div>
              )}
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState icon={FileText} title="Aucun cas de test" message="Modifiez vos critères de filtrage ou créez un nouveau cas de test." />
          ) : viewMode === 'list' ? (
            /* ---------- Liste tabulaire ---------- */
            <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
                    <th className="w-10 px-3 py-3" />
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Nom</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Description</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Tâche</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Statut</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Critique</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hidden md:table-cell">Créé le</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((tc) => (
                    <tr key={tc.id} onClick={() => openDetail(tc.id)} className="border-b border-gray-100 dark:border-gray-700/50 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                      <td className="px-3 py-3">
                        <button onClick={(e) => { e.stopPropagation(); toggleCheck(tc.id); }} className="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
                          style={{ borderColor: checked.includes(tc.id) ? '#8b5cf6' : '#d1d5db', backgroundColor: checked.includes(tc.id) ? '#8b5cf6' : 'transparent' }}>
                          {checked.includes(tc.id) && <Check className="h-3 w-3 text-white" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-800 dark:text-gray-100">{tc.name}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{tc.testCaseId}</p>
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell"><p className="text-gray-600 dark:text-gray-400 truncate max-w-[250px]">{tc.description || 'Aucune description'}</p></td>
                      <td className="px-4 py-3 hidden md:table-cell"><ItemBadge item={tc.item} /></td>
                      <td className="px-4 py-3">
                        {tc.isActive
                          ? <Badge color="#10B981"><CheckCircle className="h-3 w-3" />Actif</Badge>
                          : <Badge><XCircle className="h-3 w-3" />Inactif</Badge>}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell">{tc.isCritical ? <Badge color="#F59E0B"><AlertTriangle className="h-3 w-3" />Critique</Badge> : <span className="text-xs text-gray-400 dark:text-gray-500">Non</span>}</td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 hidden md:table-cell">{tc.creationDate}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={(e) => { e.stopPropagation(); openDetail(tc.id); }} className="p-1.5 rounded-lg text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors" title="Voir détails"><Eye className="h-4 w-4" /></button>
                          <button onClick={(e) => { e.stopPropagation(); duplicate(tc); }} className="p-1.5 rounded-lg text-gray-400 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors" title="Dupliquer"><Copy className="h-4 w-4" /></button>
                          <button onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors" title="Modifier"><Edit className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            /* ---------- Vue grille (cartes) ---------- */
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filtered.map((tc) => (
                <div key={tc.id} onClick={() => openDetail(tc.id)} className="relative h-full flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:shadow-lg cursor-pointer transition-shadow overflow-hidden">
                  <button onClick={(e) => { e.stopPropagation(); toggleCheck(tc.id); }} className={`absolute top-3 left-3 z-10 w-7 h-7 rounded-lg flex items-center justify-center shadow transition-colors ${checked.includes(tc.id) ? 'bg-violet-500 text-white' : 'bg-white/80 dark:bg-gray-700/80 text-gray-500'}`}>
                    {checked.includes(tc.id) ? <Check className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
                  </button>
                  <div className="p-4 pb-2">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shrink-0"><FileText className="h-5 w-5 text-white" /></span>
                      <div className="flex-1 min-w-0 ml-2">
                        <p className="font-semibold text-sm text-gray-800 dark:text-gray-100 truncate">{tc.name}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">{tc.testCaseId}</p>
                      </div>
                    </div>
                    <div className="flex gap-1.5 mt-2 flex-wrap"><StatusPills tc={tc} /></div>
                  </div>
                  <div className="px-4 pb-2 flex-1">
                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-3">{tc.description || 'Aucune description'}</p>
                    <div className="mt-2"><ItemBadge item={tc.item} /></div>
                    <div className="mt-3"><p className="text-[10px] text-gray-400 dark:text-gray-500">Créé le</p><p className="text-xs font-medium text-gray-700 dark:text-gray-300">{tc.creationDate}</p></div>
                  </div>
                  <div className="px-4 py-2.5 border-t border-gray-100 dark:border-gray-700/50 flex items-center gap-1">
                    <button onClick={(e) => { e.stopPropagation(); openDetail(tc.id); }} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"><Eye className="h-3.5 w-3.5" />Détails</button>
                    <button onClick={(e) => { e.stopPropagation(); duplicate(tc); }} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs text-gray-500 hover:text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-900/20 transition-colors"><Copy className="h-3.5 w-3.5" />Dupliquer</button>
                    <button onClick={(e) => e.stopPropagation()} className="flex-1 flex items-center justify-center gap-1 py-1.5 rounded-lg text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"><Edit className="h-3.5 w-3.5" />Modifier</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Barre d'action groupée (sélection multiple → assignation campagne) */}
        {checked.length > 0 && mode === 'list' && (
          <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 bg-violet-50 dark:bg-violet-900/20 flex items-center justify-between">
            <span className="text-sm font-medium text-violet-700 dark:text-violet-300">{checked.length} sélectionné{checked.length > 1 ? 's' : ''}</span>
            <div className="flex gap-2">
              <Button size="sm" className="bg-violet-500 hover:bg-violet-600 text-white"><Link2 className="h-4 w-4" />Assigner à une campagne</Button>
              <Button size="sm" variant="ghost" onClick={() => setChecked([])}><X className="h-4 w-4" />Désélectionner</Button>
            </div>
          </div>
        )}
      </Card>

      {/* ===== Guide utilisateur ===== */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Guide utilisateur</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><Plus className="h-5 w-5 text-violet-500" />Créer un cas de test</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Cliquer sur <strong>« Nouveau cas de test »</strong> (en haut à droite). <button onClick={openCreate} className="text-violet-600 dark:text-violet-400 underline">Essayer</button></GuideStep>
              <GuideStep n={2}>Saisir le <strong>Nom</strong> (obligatoire), puis Description, Pré-conditions et Résultat attendu.</GuideStep>
              <GuideStep n={3}>Choisir l'<strong>élément exécuté</strong>, renseigner les paramètres puis <strong>Créer</strong>.</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><GitBranch className="h-5 w-5 text-sky-500" />Choisir tâche ou flux</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Dans le formulaire, basculer entre <strong>Tâche simple</strong> et <strong>TASK_FLOW</strong>. <button onClick={openCreate} className="text-violet-600 dark:text-violet-400 underline">Ouvrir</button></GuideStep>
              <GuideStep n={2}>Sélectionner la <strong>tâche</strong> OU le <strong>flux</strong> dans la liste.</GuideStep>
              <GuideStep n={3}>Les deux sont <strong>exclusifs</strong> : changer de type efface la sélection précédente (task XOR taskFlow).</GuideStep>
            </ol>
          </CardContent></Card>

          <Card><CardContent>
            <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3"><AlertTriangle className="h-5 w-5 text-amber-500" />Filtrer et consulter</h3>
            <ol className="space-y-2">
              <GuideStep n={1}>Filtrer la liste par <strong>Statut</strong>, <strong>Criticité</strong> ou <strong>Tâche</strong>. <button onClick={() => setCriticalFilter('critical')} className="text-violet-600 dark:text-violet-400 underline">Critiques</button></GuideStep>
              <GuideStep n={2}>Cliquer une ligne pour ouvrir le <strong>détail</strong> et ses onglets <strong>Informations / Entrée / Sortie</strong>.</GuideStep>
              <GuideStep n={3}>Cocher des cas pour les <strong>assigner à une campagne</strong> en lot.</GuideStep>
            </ol>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== Référence des champs ===== */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Info className="h-5 w-5 text-violet-500" />Référence des champs du cas de test</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                <tr>{['Champ', 'Type', 'Requis', 'Détails'].map((h) => <th key={h} className="text-left font-semibold px-4 py-2">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800 text-gray-700 dark:text-gray-200">
                {[
                  ['name', 'Texte', 'Oui', 'Nom du cas de test'],
                  ['description', 'Texte', 'Non', 'Objectif du cas de test'],
                  ['preconditions', 'Texte', 'Non', 'Conditions préalables à l\'exécution'],
                  ['expectedResult', 'Texte', 'Non', 'Résultat attendu après exécution'],
                  ['executionDuration', 'Nombre', 'Non', 'Durée estimée en minutes (défaut 30)'],
                  ['task', 'Référence', 'XOR', 'Tâche exécutée — exclusif avec taskFlow'],
                  ['taskFlow', 'Référence', 'XOR', 'Enchaînement (TASK_FLOW) exécuté — exclusif avec task'],
                  ['isActive', 'Booléen', 'Non', 'Actif par défaut'],
                  ['isCritical', 'Booléen', 'Non', 'Marque le cas comme critique'],
                  ['inputParam', 'Map', 'Non', 'Paramètres d\'entrée (clé/valeur)'],
                  ['outputParam', 'Map', 'Non', 'Paramètres de sortie (clé/valeur)'],
                ].map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 font-medium font-mono">{r[0]}</td><td className="px-4 py-2">{r[1]}</td>
                    <td className="px-4 py-2">{r[2] === 'Oui' ? <Badge color="#EF4444">Oui</Badge> : r[2] === 'XOR' ? <Badge color="#8B5CF6">XOR</Badge> : <Badge>Non</Badge>}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{r[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* ===== Règles de gestion + API ===== */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Règles de gestion</h3>
          <ul className="space-y-2 text-sm text-gray-700 dark:text-gray-300">
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Le <strong>Nom</strong> est obligatoire ainsi qu'un <strong>élément exécuté</strong> (tâche ou flux).</li>
            <li className="flex items-start gap-2"><XCircle className="h-4 w-4 text-red-500 mt-0.5 shrink-0" />Un cas de test référence <strong>soit une tâche, soit un TASK_FLOW</strong> — jamais les deux (<strong>task XOR taskFlow</strong>).</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Côté serveur, si un <strong>taskFlow</strong> est fourni, la référence <strong>task</strong> est automatiquement vidée.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />La <strong>référence</strong> (testCaseId) et la <strong>date de création</strong> sont générées et conservées par le système.</li>
            <li className="flex items-start gap-2"><CheckCircle className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />Un cas de test peut être <strong>assigné à une campagne</strong> (en lot) pour être exécuté.</li>
          </ul>
        </CardContent></Card>

        <Card><CardContent>
          <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Endpoints API</h3>
          <ul className="space-y-1.5 text-sm font-mono text-gray-700 dark:text-gray-200">
            {[
              ['GET', '#10B981', '/api/automata/testcases'],
              ['GET', '#10B981', '/api/automata/testcases/{id}'],
              ['GET', '#10B981', '/api/automata/testcases/search?query='],
              ['POST', '#3B82F6', '/api/automata/testcases'],
              ['PUT', '#F59E0B', '/api/automata/testcases/{id}'],
              ['DELETE', '#EF4444', '/api/automata/testcases/{id}'],
              ['POST', '#3B82F6', '/api/automata/campaigns/{id}/testcases/{testCaseId}'],
            ].map((r, i) => (
              <li key={i} className="flex items-center gap-2"><Badge color={r[1]}>{r[0]}</Badge><span className="break-all">{r[2]}</span></li>
            ))}
          </ul>
          <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><ArrowRight className="h-3 w-3" />Source : automata_campaign / TestCaseController (assignation via CampaignController)</p>
        </CardContent></Card>
      </div>
    </div>
  );
};

export default TestCaseLibraryPOC;
